import { buildCopilotHeaders } from './copilotSession';

// ---------------------------------------------------------------------------
// Copilot PDF reports via the dashboard-api proxy (dashboard-api PR #908):
//   GET /API/v2/copilot/reports/{narrative_id}/{report_uuid}
// Bearer auth, streams the PDF inline with Content-Disposition exposed. There
// is no presigned/attachment variant, so both preview and download go through
// fetch → Blob. The links the agent pastes into the chat identify the report
// by the same {narrative_id}/{uuid} pair, which is all we need — the presigned
// URL itself expires within the hour and is never used.
// ---------------------------------------------------------------------------

export const REPORT_UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const REPORT_REF_RE = new RegExp(`/(?:dl|reports)/(\\d+)/(${REPORT_UUID})(?:\\.pdf)?(?:[?#]|$)`, 'i');

/** `{narrativeId, reportId}` from a `/dl/…` or presigned `/reports/…` link, or null. */
export function parseReportRef(url) {
  const match = url ? REPORT_REF_RE.exec(url) : null;
  if (!match) return null;
  return { narrativeId: Number(match[1]), reportId: match[2].toLowerCase() };
}

const REPORT_REF_ALL_RE = new RegExp(`/(?:dl|reports)/(\\d+)/(${REPORT_UUID})`, 'gi');

/** The last report referenced in a message, or null — a revised report supersedes the earlier one. */
export function findLastReportRef(text) {
  let last = null;
  for (const match of (text ?? '').matchAll(REPORT_REF_ALL_RE)) {
    last = { narrativeId: Number(match[1]), reportId: match[2].toLowerCase() };
  }
  return last;
}

export function isSameReportRef(a, b) {
  return Boolean(a && b) && a.narrativeId === b.narrativeId && a.reportId === b.reportId;
}

export function buildReportApiUrl({ narrativeId, reportId }) {
  return `/API/v2/copilot/reports/${narrativeId}/${reportId}`;
}

export class ReportFetchError extends Error {
  constructor(status) {
    super(`Copilot report request failed: ${status}`);
    this.name = 'ReportFetchError';
    this.status = status;
  }
}

// The API names the file `case-report-<case name>.pdf`; non-Latin names come
// through only in the RFC 5987 `filename*` form, the plain one is underscores.
function fileNameFromDisposition(header) {
  if (!header) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // fall through to the plain name
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : null;
}

const REPORT_TIMEOUT_MS = 120_000;

// Same deadline API.ts gives every request; this fetch goes around API.ts so
// the Blob can be read, so a stalled download would otherwise hang the
// preview/download button forever. The caller's signal (panel unmount) still
// aborts early.
function withTimeout(signal) {
  const timeoutSignal = AbortSignal.timeout(REPORT_TIMEOUT_MS);
  if (!signal) return timeoutSignal;
  const combined = new AbortController();
  for (const source of [signal, timeoutSignal]) {
    if (source.aborted) {
      combined.abort(source.reason);
      break;
    }
    source.addEventListener('abort', () => combined.abort(source.reason), { once: true });
  }
  return combined.signal;
}

export async function fetchCopilotReport(ref, { signal } = {}) {
  const response = await fetch(buildReportApiUrl(ref), {
    method: 'GET',
    headers: buildCopilotHeaders(),
    signal: withTimeout(signal),
  });
  if (!response.ok) throw new ReportFetchError(response.status);
  const blob = await response.blob();
  return {
    blob,
    fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')) || `case-report-${ref.reportId}.pdf`,
  };
}

// Which message to show for a failed fetch. 404 covers both "no such report"
// and "no access" (the API deliberately doesn't distinguish); anything else is
// the service being unavailable, so suggest trying again rather than giving up.
export function reportErrorMessage(error, t) {
  if (error?.status === 404) return t("Report not found or you don't have access to it.");
  return t('The report is temporarily unavailable. Please try again later.');
}

export function saveBlob(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a tick to start the download before the URL goes away.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
